import { useEffect, useState, useCallback } from "react";
import {
    CheckCircle2, Star, Heart, PartyPopper, Sparkles, ThumbsUp, Gift, Award,
} from "lucide-react";

const ICON_MAP = {
    check: CheckCircle2, star: Star, heart: Heart, party: PartyPopper,
    sparkles: Sparkles, thumbs: ThumbsUp, gift: Gift, award: Award,
};

// Global celebration pop-up. Mounted once at app root; listens for "vt:celebrate".
// Title, icon and auto-dismiss come from the fired event (admin-configurable).
export default function SuccessCelebration() {
    const [state, setState] = useState({ open: false, message: "", title: "Success!", icon: "check", dismiss: 2600 });

    const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

    useEffect(() => {
        const onCelebrate = (e) => {
            const d = e?.detail || {};
            setState({
                open: true,
                message: d.message || "Done!",
                title: d.title || "Success!",
                icon: ICON_MAP[d.icon] ? d.icon : "check",
                dismiss: Number(d.dismiss) > 0 ? Number(d.dismiss) : 2600,
            });
        };
        window.addEventListener("vt:celebrate", onCelebrate);
        return () => window.removeEventListener("vt:celebrate", onCelebrate);
    }, []);

    useEffect(() => {
        if (!state.open) return undefined;
        const t = setTimeout(close, state.dismiss);
        return () => clearTimeout(t);
    }, [state.open, state.dismiss, close]);

    if (!state.open) return null;

    const Icon = ICON_MAP[state.icon] || CheckCircle2;
    const dots = ["#1B5E20", "#F57C00", "#25D366", "#1565C0", "#F9A825", "#8E24AA"];

    return (
        <div
            data-testid="success-celebration"
            className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
            onClick={close}
            style={{ background: "rgba(15,51,17,0.28)", backdropFilter: "blur(3px)" }}
        >
            <div
                className="relative rounded-3xl bg-white shadow-2xl px-8 py-8 text-center max-w-sm w-full"
                style={{ animation: "vtPop .38s cubic-bezier(.18,.89,.32,1.28)" }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* confetti burst */}
                <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
                    {dots.map((c, i) => (
                        <span
                            key={i}
                            className="absolute h-2 w-2 rounded-full"
                            style={{
                                background: c,
                                left: `${18 + i * 12}%`,
                                animation: `vtConfetti .9s ease-out ${i * 0.05}s both`,
                            }}
                        />
                    ))}
                </div>

                <div
                    className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                    style={{ background: "#E8F5E9", animation: "vtCheck .5s ease-out" }}
                >
                    <Icon className="h-9 w-9 text-[#1B5E20]" />
                </div>
                <h3 data-testid="success-celebration-title" className="text-[19px] font-bold text-[#0F3311]">{state.title}</h3>
                <p className="mt-1 text-[13.5px] text-[#5A6B5C]">{state.message}</p>
                <button
                    data-testid="success-celebration-ok"
                    onClick={close}
                    className="mt-5 inline-flex h-10 items-center justify-center rounded-full bg-[#1B5E20] px-8 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#154d1a]"
                >
                    Great
                </button>
            </div>

            <style>{`
                @keyframes vtPop { 0%{opacity:0;transform:scale(.82) translateY(8px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
                @keyframes vtCheck { 0%{transform:scale(.4);opacity:0} 60%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1} }
                @keyframes vtConfetti { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(46px) scale(.6)} }
            `}</style>
        </div>
    );
}
